// ANGULAR
import { OverlayModule } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// EXTERNAL MODULES
import { firstValueFrom } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFireDatabaseModule } from '@angular/fire/compat/database';
import { AngularFireMessagingModule } from '@angular/fire/compat/messaging';
import { QuillModule } from 'ngx-quill'

// HAMMER JS
import 'hammerjs';

// NGX PERMISSIONS
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';

// LAYOUT SERVICES
import {
  LayoutConfigService,
  SplashScreenService,
} from './@metronic/core/_base/layout';

// CONFIG
import { LayoutConfig } from './@metronic/core/_config/layout.config';

// INTERCEPTORS AND ERROR HANDLERS
import { AuthInterceptor } from './core/interceptors/auth-interceptor';
import { GlobalErrorHandler } from './core/error-handler/global-error-handler';

// MODULES
import { CoreModule } from './modules/core/core.module';
import { UiModulesRouting } from './modules/ui-modules-routing.module';
import { StateModule } from './state/state.module';

// COMPONENTS

import { SplashScreenComponent } from './@metronic/views/partials/layout';
import { AppComponent } from './app.component';
import { TokenStorageService } from './state/core/services/token-storage/token-storage.service';
import { AuthService } from './state/modules/auth/services/auth.service';
import { environment } from '../environments/environment';
import { MessagingService } from './core/services/messaging.service';

// inline svg
import { AngularSvgIconModule } from 'angular-svg-icon';
import { PermissionUtil } from './core/utils/PermissionUtil ';

// ng-bootstrap global config
import { NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';

export function initializeLayoutConfig(authStoreService: AuthService, permissionService: NgxPermissionsService, tokenStorageService: TokenStorageService, appConfig: LayoutConfigService) {
  // initialize app by loading default demo layout config
  return () => {
    // set config
    if (appConfig.getConfig() === null) {
      appConfig.loadConfigs(new LayoutConfig().configs);
    }
    if (tokenStorageService.validateAccessToken() && tokenStorageService.validateBusinessToken()) {
      return firstValueFrom(authStoreService.getUserAndBusinessV2(null)).then(data => {
        if (data && data.data && data.data.business_feature_permission && data.data.business_feature_permission.feature_permissions) {
          const permissions = data.data.business_feature_permission.feature_permissions;
          const permissionNames = PermissionUtil.setPermission(permissions)
          PermissionUtil.setCLA(data.data.business_feature_permission.business_preference)

         // console.log(permissionNames, 'permissionNames')
          permissionService.flushPermissions();
          permissionService.loadPermissions(permissionNames);
        }
        return Promise.resolve(true);
      }).catch(() => Promise.resolve(false));
    } else {
      return Promise.resolve(true);
    }
  };

}

@NgModule({
  declarations: [AppComponent, SplashScreenComponent],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    CoreModule,
    UiModulesRouting,
    HttpClientModule,
    OverlayModule,
    StateModule,
    AngularFireDatabaseModule,
    AngularFireAuthModule,
    AngularFireMessagingModule,
    AngularFireModule.initializeApp(environment.firebaseConfig),
    NgxPermissionsModule.forRoot(),
    TranslateModule.forRoot(),
    QuillModule.forRoot(),
    AngularSvgIconModule.forRoot(),
  ],
  exports: [],
  providers: [
    LayoutConfigService,
    MessagingService,
    SplashScreenService,
    {
      // layout config initializer
      provide: APP_INITIALIZER,
      useFactory: initializeLayoutConfig,
      deps: [AuthService, NgxPermissionsService, TokenStorageService, LayoutConfigService], multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    // { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(tooltipConfig: NgbTooltipConfig) {
    // App-wide tooltip behaviour. Default ng-bootstrap triggers (hover) leave a
    // tooltip stuck open when a click covers or re-renders the trigger (e.g. a
    // loading overlay) so mouseleave never fires. Adding the `click` toggle makes
    // the click that starts the action also dismiss the (already-open-on-hover)
    // tooltip. NgbTooltipConfig is root-provided, so this applies everywhere.
    tooltipConfig.triggers = 'mouseenter:mouseleave click';
  }
}
